import { useCallback, useEffect, useState, useRef } from 'react';
import { UserGroup, FilterGroup } from '../../../models';
import { apiClient, apiErrorMessage } from '../../../utils';
import { Input, Spin, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FilterViewer } from '../../../elements';

const { Text } = Typography;

type PermissionData = {
  role2Id: string;
  role2Name: string;
  role2Description: string;
  role2PermissionId: string;
  effect: 'ALLOW' | 'DENY';
  target: string;
  targetLabel: string;
  filterGroup?: FilterGroup;
  filterGroupDetails?: any;
};

type PermissionRoleSource = {
  roleId: string;
  roleName: string;
  filterGroup?: FilterGroup;
};

type PermissionRow = {
  target: string;
  targetLabel: string;
  roles: PermissionRoleSource[];
};

interface PaginatedUserGroupPermissionsResponse {
  data: PermissionData[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export function UserGroupPermissionsPage(props: { userGroup?: UserGroup }) {
  const [userGroupPermissions, setUserGroupPermissions] = useState<PermissionData[]>([]);
  const [total, setTotal] = useState(0);
  const [userGroupPermissionsLoading, setUserGroupPermissionsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(false);

  // Virtual scrolling constants
  const ROLE_LINE_HEIGHT = 32; // Height per role line
  const ROW_VERTICAL_PADDING = 8; // Vertical padding for each row
  const BUFFER_SIZE = 10; // Number of items to render beyond visible area
  const MAX_DOM_ITEMS = 100; // Maximum items to keep in DOM

  // Calculate height for each permission row
  const getItemHeight = (permissionRow: PermissionRow) => {
    const roleCount = Math.max(1, permissionRow.roles?.length || 0);
    return roleCount * ROLE_LINE_HEIGHT + (ROW_VERTICAL_PADDING * 2);
  };

  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [visibleEndIndex, setVisibleEndIndex] = useState(MAX_DOM_ITEMS);

  // Load user group permissions
  const loadUserGroupPermissions = useCallback(
    async (pageNum: number, searchTerm: string, append = false) => {
      if (loadingRef.current || !props.userGroup?.id) return;

      loadingRef.current = true;
      setUserGroupPermissionsLoading(true);

      try {
        const response = await apiClient.get<PaginatedUserGroupPermissionsResponse>(
          `authorization/permissions/user-group/${props.userGroup.id}/paginated`,
          {
            params: {
              page: pageNum,
              pageSize: 50,
              search: searchTerm,
            },
          },
        );

        if (append) {
          setUserGroupPermissions((prev) => [...prev, ...response.data.data]);
        } else {
          setUserGroupPermissions(response.data.data);
          setVisibleStartIndex(0);
          setVisibleEndIndex(MAX_DOM_ITEMS);
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }

        setTotal(response.data.total);
        setHasMore(response.data.hasMore);
        setPage(pageNum);
      } catch (error) {
        apiErrorMessage(error);
      } finally {
        setUserGroupPermissionsLoading(false);
        loadingRef.current = false;
      }
    },
    [props.userGroup?.id],
  );

  // Initialize on user group change
  useEffect(() => {
    if (props.userGroup?.id) {
      setSearch('');
      setSearchInput('');
      setPage(1);
      setUserGroupPermissions([]);
      loadUserGroupPermissions(1, '');
    }
  }, [props.userGroup?.id, loadUserGroupPermissions]);

  // Handle search
  const handleSearch = useCallback(() => {
    setSearch(searchInput);
    setPage(1);
    setUserGroupPermissions([]);
    loadUserGroupPermissions(1, searchInput);
  }, [searchInput, loadUserGroupPermissions]);

  // Group permissions by target
  const groupedPermissions = useCallback(() => {
    if (!userGroupPermissions || userGroupPermissions.length === 0) return [];

    const grouped = new Map<string, PermissionRow>();

    userGroupPermissions.forEach(permission => {
      const key = permission.target || permission.targetLabel || permission.role2PermissionId;

      if (!key) {
        return;
      }

      if (!grouped.has(key)) {
        grouped.set(key, {
          target: permission.target ?? key,
          targetLabel: permission.targetLabel ?? permission.target ?? key,
          roles: [],
        });
      }

      const current = grouped.get(key);

      if (!current) {
        return;
      }

      const roleKey = `${permission.role2Id}`;
      const roleExists = current.roles.some(role => role.roleId === roleKey);

      if (!roleExists) {
        current.roles.push({
          roleId: permission.role2Id,
          roleName: permission.role2Name,
          filterGroup: permission.filterGroup,
        });
      }
    });

    return Array.from(grouped.values());
  }, [userGroupPermissions]);

  const permissionRows = groupedPermissions();

  // Handle scroll for lazy loading
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop: currentScrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;

    // Calculate visible range for virtual scrolling based on cumulative heights
    let accumulatedHeight = 0;
    let startIndex = 0;
    for (let i = 0; i < permissionRows.length; i++) {
      const itemHeight = getItemHeight(permissionRows[i]);
      if (accumulatedHeight + itemHeight > currentScrollTop) {
        startIndex = Math.max(0, i - BUFFER_SIZE);
        break;
      }
      accumulatedHeight += itemHeight;
    }

    const endIndex = Math.min(permissionRows.length, startIndex + MAX_DOM_ITEMS);

    setVisibleStartIndex(startIndex);
    setVisibleEndIndex(endIndex);

    // Load more when near bottom
    if (scrollHeight - currentScrollTop - clientHeight < 200 && hasMore && !userGroupPermissionsLoading && !loadingRef.current) {
      loadUserGroupPermissions(page + 1, search, true);
    }
  }, [permissionRows, hasMore, userGroupPermissionsLoading, page, search, loadUserGroupPermissions]);

  // Get visible permissions for rendering
  const visiblePermissions = permissionRows.slice(visibleStartIndex, visibleEndIndex);

  // Calculate offset and total height
  let offsetY = 0;
  for (let i = 0; i < visibleStartIndex; i++) {
    offsetY += getItemHeight(permissionRows[i]);
  }

  let totalHeight = 0;
  for (let i = 0; i < permissionRows.length; i++) {
    totalHeight += getItemHeight(permissionRows[i]);
  }

  return (
    <div>
      <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Input
          placeholder="Yetki ara..."
          prefix={<SearchOutlined />}
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          onPressEnter={handleSearch}
          allowClear
          style={{ width: 300 }}
        />
        <button
          onClick={handleSearch}
          style={{
            padding: '4px 15px',
            background: '#1890ff',
            color: 'white',
            border: 'none',
            borderRadius: '2px',
            cursor: 'pointer',
          }}
        >
          Ara
        </button>
        <Text type="secondary" style={{ marginLeft: '8px' }}>
          Toplam: {total}
        </Text>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        style={{
          maxHeight: '600px',
          overflowY: 'auto',
          position: 'relative',
          border: '1px solid #f0f0f0',
        }}
      >
        {permissionRows.length === 0 && !userGroupPermissionsLoading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <Text type="secondary">Bu kullanıcı grubunda yetki bulunmamaktadır.</Text>
          </div>
        ) : (
          <div>
            {/* Table Header */}
            <div
              style={{
                display: 'flex',
                background: '#fafafa',
                borderBottom: '2px solid #e8e8e8',
                fontSize: '12px',
                fontWeight: 600,
                color: '#666',
                position: 'sticky',
                top: 0,
                zIndex: 2,
              }}
            >
              <div style={{
                minWidth: '600px',
                flex: '1 1 600px',
                padding: '10px 12px',
                borderRight: '1px solid #e8e8e8'
              }}>
                Yetki
              </div>
              <div style={{
                minWidth: '150px',
                flex: '1 1 150px',
                padding: '10px 12px',
                borderRight: '1px solid #e8e8e8'
              }}>
                Roller
              </div>
              <div style={{
                minWidth: '250px',
                flex: '1 1 250px',
                padding: '10px 12px'
              }}>
                Filtreler
              </div>
            </div>

            {/* Virtual scroll container */}
            <div style={{ height: totalHeight, position: 'relative' }}>
              {/* Visible items with offset */}
              <div style={{ transform: `translateY(${offsetY}px)` }}>
                {visiblePermissions.map((permissionRow, index) => {
                  const actualIndex = visibleStartIndex + index;
                  let dataSetId = '';
                  if (permissionRow.target.startsWith('dataSets.')) {
                    const parts = permissionRow.target.split('.');
                    dataSetId = parts[1];
                  }

                  const itemHeight = getItemHeight(permissionRow);

                  return (
                    <div
                      key={permissionRow.target}
                      style={{
                        display: 'flex',
                        background: actualIndex % 2 === 0 ? 'white' : '#fafafa',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '13px',
                        height: itemHeight,
                      }}
                    >
                      {/* Permission Label Column */}
                      <div style={{
                        minWidth: '600px',
                        flex: '1 1 600px',
                        padding: `${ROW_VERTICAL_PADDING}px 12px`,
                        display: 'flex',
                        alignItems: 'center',
                        borderRight: '1px solid #f0f0f0'
                      }}>
                        {permissionRow.targetLabel}
                      </div>

                      {/* Roles Column */}
                      <div style={{
                        minWidth: '150px',
                        flex: '1 1 150px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        borderRight: '1px solid #f0f0f0'
                      }}>
                        {permissionRow.roles?.length ? (
                          permissionRow.roles.map((role, roleIdx) => (
                            <div
                              key={`${role.roleId}-role`}
                              style={{
                                display: 'flex',
                                height: ROLE_LINE_HEIGHT,
                                alignItems: 'center',
                                padding: '0 12px'
                              }}
                            >
                              {role.roleName}
                            </div>
                          ))
                        ) : (
                          <div style={{
                            display: 'flex',
                            height: ROLE_LINE_HEIGHT,
                            alignItems: 'center',
                            padding: '0 12px'
                          }}>-</div>
                        )}
                      </div>

                      {/* Filters Column */}
                      <div style={{
                        minWidth: '250px',
                        flex: '1 1 250px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                      }}>
                        {permissionRow.roles?.length ? (
                          permissionRow.roles.map((role, roleIdx) => (
                            <div
                              key={`${role.roleId}-filter`}
                              style={{
                                display: 'flex',
                                height: ROLE_LINE_HEIGHT,
                                alignItems: 'center',
                                padding: '0 12px'
                              }}
                            >
                              {role.filterGroup && dataSetId !== '' ? (
                                <FilterViewer dataSetId={dataSetId} filterGroup={role.filterGroup} size="small" />
                              ) : (
                                '-'
                              )}
                            </div>
                          ))
                        ) : (
                          <div style={{
                            display: 'flex',
                            height: ROLE_LINE_HEIGHT,
                            alignItems: 'center',
                            padding: '0 12px'
                          }}>-</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Loading indicator */}
            {userGroupPermissionsLoading && (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <Spin />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
